import DOMPurify from 'dompurify';
import { Marked } from 'marked';

/**
 * Safe Markdown rendering (Section 3.1): raw HTML is not rendered, links are sanitized and open
 * safely, remote images are never embedded (shown as links), and DOMPurify is the final guard.
 */
const marked = new Marked({
  gfm: true,
  breaks: true,
  async: false,
  renderer: {
    html({ text }) {
      return escapeHtml(text);
    },
    image({ href, text }) {
      const label = escapeHtml(text || href || 'image');
      return safeHref(href) ? `<a href="${escapeHtml(href)}" rel="noopener noreferrer nofollow" target="_blank">🖼 ${label}</a>` : label;
    },
    link({ href, tokens }) {
      const inner = this.parser.parseInline(tokens);
      if (!safeHref(href)) return inner;
      return `<a href="${escapeHtml(href)}" rel="noopener noreferrer nofollow" target="_blank">${inner}</a>`;
    },
  },
});

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

function safeHref(href: string | null | undefined): boolean {
  if (!href) return false;
  if (href.startsWith('/') && !href.startsWith('//')) return true;
  try {
    const url = new URL(href);
    return ['http:', 'https:', 'mailto:'].includes(url.protocol);
  } catch {
    return false;
  }
}

/** Mention syntax @[Name](user:uuid) renders as plain highlighted text. */
function renderMentions(markdown: string): string {
  return markdown.replace(/@\[([^\]]{1,80})\]\(user:[0-9a-f-]{36}\)/gi, (_, name: string) => `**@${name}**`);
}

/** Tracker keys become internal links. */
function linkKeys(html: string): string {
  return html.replace(/(^|[\s(>])((?:VAM|REQ)-\d{1,9})(?=$|[\s).,;:!?<])/g, (_, pre: string, key: string) => `${pre}<a href="/go/${key}" data-key-link="true">${key}</a>`);
}

export function renderMarkdown(markdown: string): string {
  const html = marked.parse(renderMentions(markdown)) as string;
  return DOMPurify.sanitize(linkKeys(html), {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'del', 'code', 'pre', 'blockquote', 'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'h4', 'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'input'],
    ALLOWED_ATTR: ['href', 'rel', 'target', 'type', 'checked', 'disabled', 'data-key-link'],
    ALLOW_DATA_ATTR: false,
  });
}
